import type { AuthType } from '@contexts/auth-provider/AuthProvider.types';

import { useAuth } from '@contexts/auth-provider';
import { useMsalAuth } from '@contexts/msal-provider';
import { getCurrentDeepLinkUrl } from '@shared/helpers/deep-link/getCurrentDeepLinkUrl';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type {
  AuthStrategyResult,
  UseAuthStrategyReturn,
} from './useAuthStrategy.types';

import {
  determineAuthStrategy,
  getReturnUrlFromStorage,
  isAuthTypeMismatch,
} from './useAuthStrategy.utils';

/**
 * Hook for handling authentication strategy logic
 *
 * Responsibilities:
 * - Determine auth type from URL pattern
 * - Start the login redirect (or send Manage users to the landing page to pick B2C/B2E)
 * - Handle deep link storage decisions
 * - Detect auth type mismatches (e.g. B2E user on a B2C-only route)
 *
 * Cached credentials are already validated by AuthProvider (refresh-token renewal),
 * so there is no separate silent-SSO step: unauthenticated means "start login".
 */
export function useAuthStrategy(): UseAuthStrategyReturn {
  const { authData, isAuthenticated } = useAuth();
  const { login } = useMsalAuth();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Check if the authenticated user's auth type doesn't match the route.
   * Train requires B2C (external), Instruct requires B2E (internal).
   */
  const hasAuthTypeMismatch = useMemo(() => {
    if (!isAuthenticated || !authData) return false;
    const mismatch = isAuthTypeMismatch(
      location.pathname,
      authData.isInternalUser
    );
    return mismatch;
  }, [isAuthenticated, authData, location.pathname]);

  /**
   * Get deep link URL for post-login navigation
   * Priority:
   * 1. returnUrl from sessionStorage (set by interceptor on session expiry)
   * 2. Current URL (if not on landing/auth pages)
   */
  const getDeepLinkUrl = useCallback(
    (): string | undefined =>
      getReturnUrlFromStorage() ?? getCurrentDeepLinkUrl(),
    []
  );

  const getAuthStrategy = useCallback((path: string): AuthStrategyResult => {
    return determineAuthStrategy(path);
  }, []);

  const startLogin = useCallback(
    async (preferredAuthType?: AuthType): Promise<void> => {
      const currentPath = location.pathname;
      const deepLinkUrl = getDeepLinkUrl();

      if (!currentPath.startsWith('/manage')) {
        await login(getAuthStrategy(currentPath).authType, deepLinkUrl);
        return;
      }

      if (preferredAuthType) {
        await login(preferredAuthType, deepLinkUrl);
        return;
      }

      // Auth type is unknown — store deep link and show landing page so the
      // user can choose B2C or B2E. The landing page login() passes undefined
      // as deepLinkUrl, so our stored value is preserved through MSAL redirect.
      if (deepLinkUrl) {
        sessionStorage.setItem('auth-deep-link', deepLinkUrl);
      }
      navigate('/', { replace: true });
    },
    [getAuthStrategy, getDeepLinkUrl, location.pathname, login, navigate]
  );

  return {
    hasAuthTypeMismatch,
    startLogin,
    getAuthStrategy,
    getDeepLinkUrl,
  };
}
